import { atom } from 'jotai'
import { currentSpaceAtom } from '@/store/auth.atom'
import type {
  SimpleSpaceEntityPricingTier as PricingTierType,
  SimpleSpaceEntityBillingCycle as BillingCycleType,
  SimpleSpaceEntityTrialStatus as TrialStatusType,
} from '@/api/models/SimpleSpaceEntity'

/**
 * Pricing Atoms (RFC-001 & RFC-002)
 *
 * State management for pricing tiers, features, and resource limits
 */

// ============================================
// Pricing Tier Types (re-export from SimpleSpaceEntity)
// ============================================

export type PricingTier = PricingTierType
export type BillingCycle = BillingCycleType
export type TrialStatus = TrialStatusType

export type FeatureKey =
  | 'sso'
  | 'calendarSync'
  | 'teamsIntegration'
  | 'customRoles'
  | 'advancedAnalytics'
  | 'advancedRules'
  | 'scim'
  | 'multiLocation'
  | 'hardware'
  | 'apiAccess'
  | 'customBranding'
  | 'sla'

// ============================================
// Pricing Configuration (mirrors backend constants)
// ============================================

export interface TierFeatures {
  sso: boolean
  calendarSync: boolean
  teamsIntegration: boolean
  customRoles: boolean
  advancedAnalytics: boolean
  advancedRules: boolean | 'basic'
  scim: boolean
  multiLocation: boolean | 'addon'
  hardware: boolean
  apiAccess: boolean
  customBranding: boolean
  sla: boolean
}

export interface ResourceLimits {
  locations: number | null
  maxLocations?: number
  desks: number | null
  rooms: number | null
  visitors: number | null
  users: number | null
}

export interface TierConfig {
  name: string
  displayName?: string
  price: {
    monthly: number | null
    annual: number | null
  }
  limits: ResourceLimits
  features: TierFeatures
  durationDays?: number
  readOnly?: boolean
  dataRetentionDays?: number
  support: 'community' | 'email' | 'priority' | 'dedicated'
}

// Frontend pricing configuration (mirrors backend)
export const PRICING_CONFIG: Record<PricingTier, TierConfig> = {
  TRIAL: {
    name: 'Trial',
    displayName: '14-Day Trial',
    price: { monthly: 0, annual: 0 },
    limits: {
      locations: 1,
      desks: 20,
      rooms: 5,
      visitors: 50,
      users: 25,
    },
    features: {
      sso: true,
      calendarSync: true,
      teamsIntegration: true,
      customRoles: true,
      advancedAnalytics: true,
      advancedRules: true,
      scim: false,
      multiLocation: false,
      hardware: false,
      apiAccess: false,
      customBranding: false,
      sla: false,
    },
    durationDays: 14,
    support: 'email',
  },
  SANDBOX: {
    name: 'Sandbox (Evaluation)',
    displayName: 'Sandbox (Evaluation)',
    price: { monthly: 0, annual: 0 },
    limits: {
      locations: 1,
      desks: 20,
      rooms: 5,
      visitors: 0,
      users: 25,
    },
    features: {
      sso: false,
      calendarSync: false,
      teamsIntegration: false,
      customRoles: false,
      advancedAnalytics: false,
      advancedRules: false,
      scim: false,
      multiLocation: false,
      hardware: false,
      apiAccess: false,
      customBranding: false,
      sla: false,
    },
    readOnly: true,
    dataRetentionDays: 90,
    support: 'community',
  },
  STARTER: {
    name: 'Starter',
    displayName: 'Workspace Starter',
    price: { monthly: 119, annual: 99 },
    limits: {
      locations: 1,
      maxLocations: 5,
      desks: 20,
      rooms: 5,
      visitors: 50,
      users: null,
    },
    features: {
      sso: false,
      calendarSync: false,
      teamsIntegration: false,
      customRoles: false,
      advancedAnalytics: false,
      advancedRules: false,
      scim: false,
      multiLocation: 'addon',
      hardware: false,
      apiAccess: false,
      customBranding: false,
      sla: false,
    },
    support: 'email',
  },
  PREMIUM: {
    name: 'Premium',
    displayName: 'Workspace Premium',
    price: { monthly: 249, annual: 199 },
    limits: {
      locations: 1,
      maxLocations: 5,
      desks: 50,
      rooms: 15,
      visitors: null,
      users: null,
    },
    features: {
      sso: true,
      calendarSync: true,
      teamsIntegration: true,
      customRoles: true,
      advancedAnalytics: true,
      advancedRules: true,
      scim: true,
      multiLocation: 'addon',
      hardware: false,
      apiAccess: false,
      customBranding: false,
      sla: false,
    },
    support: 'priority',
  },
  ENTERPRISE: {
    name: 'Enterprise',
    displayName: 'Enterprise',
    price: { monthly: null, annual: null },
    limits: {
      locations: null,
      desks: null,
      rooms: null,
      visitors: null,
      users: null,
    },
    features: {
      sso: true,
      calendarSync: true,
      teamsIntegration: true,
      customRoles: true,
      advancedAnalytics: true,
      advancedRules: true,
      scim: true,
      multiLocation: true,
      hardware: true,
      apiAccess: true,
      customBranding: true,
      sla: true,
    },
    support: 'dedicated',
  },
}

export const EXPANSION_PRICING = {
  desk: 2,
  room: 6,
  location: {
    monthly: 119,
    annual: 99,
  },
  visitor: 0.5,
} as const

// ============================================
// Atoms
// ============================================

/**
 * Current pricing tier
 * Reads from space.pricing_tier, falls back to level-based detection
 */
export const currentTierAtom = atom<PricingTier>((get) => {
  const space = get(currentSpaceAtom)
  if (!space) return 'TRIAL'

  // Check for new pricing_tier field (after migration)
  if (space.pricing_tier) return space.pricing_tier

  // Check for active trial indicators (PLG signups)
  // If trial_expires_at is set and in the future, this is a trial account
  if (space.trial_expires_at) {
    const expiresAt = new Date(space.trial_expires_at)
    if (expiresAt > new Date()) {
      return 'TRIAL'
    }
  }

  // If trial_started_at is set but no pricing_tier, assume it's a new trial
  // (trial_expires_at might not be set yet)
  if (space.trial_started_at && !space.pricing_tier) {
    return 'TRIAL'
  }

  // Fallback: map old level to new tier
  switch (space.level) {
    case 'Enterprise':
      return 'ENTERPRISE'
    case 'Premium':
      return 'PREMIUM'
    case 'Standard':
      return 'STARTER'
    case 'Free':
      return 'SANDBOX'
    default:
      return 'TRIAL'
  }
})

/**
 * Current tier configuration
 */
export const currentTierConfigAtom = atom<TierConfig>((get) => {
  const tier = get(currentTierAtom)
  return PRICING_CONFIG[tier]
})

/**
 * Current billing cycle
 */
export const currentBillingCycleAtom = atom<BillingCycle | null>((get) => {
  const space = get(currentSpaceAtom)
  return space?.billing_cycle || null
})

/**
 * Check if a feature is available for current tier
 * During trial, Premium features are enabled
 */
export const isFeatureAvailableAtom = atom((get) => (feature: FeatureKey) => {
  const tier = get(currentTierAtom)
  const config = PRICING_CONFIG[tier]
  const featureValue = config.features[feature]
  return featureValue === true || featureValue === 'addon'
})

/**
 * Check if a feature requires an upgrade
 */
export const featureRequiresUpgradeAtom = atom(
  (get) => (feature: FeatureKey) => {
    const isAvailable = get(isFeatureAvailableAtom)(feature)
    return !isAvailable
  }
)

/**
 * Get resource limit for current tier
 *
 * Uses multiplier formula for location expansion:
 * effective = base × totalLocations + expansion
 * where totalLocations = 1 (base) + expansion_locations
 */
export const getResourceLimitAtom = atom(
  (get) =>
    (resource: keyof ResourceLimits): number | null => {
      const tier = get(currentTierAtom)
      const space = get(currentSpaceAtom)
      const config = PRICING_CONFIG[tier]

      // Calculate total locations (1 base + expansion_locations)
      const totalLocations = 1 + (space?.expansion_locations || 0)

      // For desks, rooms, and visitors: use multiplier formula
      // effective = base × totalLocations + expansion
      if (resource === 'desks') {
        const base = config.limits.desks
        if (base === null) return null // Unlimited
        return base * totalLocations + (space?.expansion_desks || 0)
      }

      if (resource === 'rooms') {
        const base = config.limits.rooms
        if (base === null) return null // Unlimited
        return base * totalLocations + (space?.expansion_rooms || 0)
      }

      if (resource === 'visitors') {
        const base = config.limits.visitors
        if (base === null) return null // Unlimited
        return base * totalLocations + (space?.expansion_visitors || 0)
      }

      // For locations: return total locations (1 base + expansion_locations)
      if (resource === 'locations') {
        return totalLocations
      }

      return config.limits[resource] ?? null
    }
)

/**
 * Check if current tier is paid
 */
export const isPaidTierAtom = atom<boolean>((get) => {
  const tier = get(currentTierAtom)
  return tier === 'STARTER' || tier === 'PREMIUM' || tier === 'ENTERPRISE'
})

/**
 * 是否为 PLG 付费用户 (STARTER/PREMIUM)
 * PLG 付费用户通过 pricing_tier 确认，不需要检查 CustomerSubscription
 *
 * 用途：跳过 CheckResourceSubscriptionLayout 等布局组件的订阅检查
 * - STARTER/PREMIUM 用户已通过 PLG 流程付费，无需 CustomerSubscription 记录
 * - ENTERPRISE 用户仍需检查 CustomerSubscription（企业合同订阅）
 * - TRIAL 用户单独处理（isTrial）
 *
 * 注意：同时检查 subscription_ends_at 确保订阅未过期
 */
export const isPLGPaidUserAtom = atom<boolean>((get) => {
  const tier = get(currentTierAtom)
  const space = get(currentSpaceAtom)

  // 必须是 STARTER 或 PREMIUM tier
  if (tier !== 'STARTER' && tier !== 'PREMIUM') {
    return false
  }

  // 检查订阅是否过期
  if (space?.subscription_ends_at) {
    const endsAt = new Date(space.subscription_ends_at)
    if (endsAt < new Date()) {
      return false // 订阅已过期
    }
  }

  return true
})

/**
 * Check if in sandbox (read-only) mode
 */
export const isSandboxModeAtom = atom<boolean>((get) => {
  const tier = get(currentTierAtom)
  return tier === 'SANDBOX'
})

/**
 * Get recommended tier for upgrade
 */
export const recommendedUpgradeTierAtom = atom<PricingTier>((get) => {
  const tier = get(currentTierAtom)
  switch (tier) {
    case 'TRIAL':
    case 'SANDBOX':
      return 'STARTER'
    case 'STARTER':
      return 'PREMIUM'
    default:
      return 'ENTERPRISE'
  }
})

/**
 * Calculate annual savings percentage
 */
export const annualSavingsPercentAtom = atom<number>((get) => {
  const tier = get(currentTierAtom)
  const config = PRICING_CONFIG[tier]
  if (!config.price.monthly || !config.price.annual) return 0
  return Math.round(
    ((config.price.monthly - config.price.annual) / config.price.monthly) * 100
  )
})
